/**
 * kd-algolia-top-rankings: Top page ranking sliders with multipleQueries batch
 * Replaces assets/algolia-top-simulation.js
 *
 * @deprecated v2 リニューアル後は kd-algolia-carousel に置換済み。
 * このコンポーネントは検証ページ /pages/algolia-top-sim 専用の PoC。
 * TODO: 検証ページ廃止後にこのファイルと sections/algolia-top-sim.liquid、
 *       chunks/algolia.ts のエクスポートを削除する。
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { buildSystemFilters } from './utils/algolia-filters';
import { getAlgoliaConfig, createSearchClient, waitForAlgoliaLibs } from './utils/algolia-client';
import { parseTestParams } from './utils/algolia-test-params';
import { buildMoreUrl } from './carousel.logic';

interface RankingConfig {
  id: string;
  title: string;
  sortIndex: string;
  collectionId: string;
  filters: string;
  ruleContexts: string;
  displayNum: number;
  showRank: boolean;
}

interface RankingHit {
  title?: string;
  handle?: string;
  image?: string;
  price?: number;
  compare_at_price?: number;
  price_ratio?: number;
  tags?: string[];
}

interface SearchResult {
  hits: RankingHit[];
  nbHits: number;
  processingTimeMS: number;
}

interface RankingWithResult {
  ranking: {
    title: string;
    filters: string;
    ruleContexts: string;
    showRank: boolean;
  };
  result: SearchResult;
}

@customElement('kd-algolia-top-rankings')
export class KdAlgoliaTopRankings extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String })
  rankings = '[]';

  @state() private _results: RankingWithResult[] = [];
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _cacheEnabled = false;

  private _parsedRankings: RankingConfig[] = [];

  async connectedCallback(): Promise<void> {
    super.connectedCallback();

    this._cacheEnabled = parseTestParams().cacheEnabled;

    try {
      // Try attribute first, then window global
      const attrRankings = this.rankings !== '[]' ? JSON.parse(this.rankings) : [];
      this._parsedRankings = attrRankings.length > 0
        ? attrRankings
        : ((window as unknown as Record<string, unknown>).__ALGOLIA_TOP_RANKINGS__ as RankingConfig[] || []);
    } catch {
      this._parsedRankings = (window as unknown as Record<string, unknown>).__ALGOLIA_TOP_RANKINGS__ as RankingConfig[] || [];
    }

    // Fallback if no rankings configured
    if (this._parsedRankings.length === 0) {
      this._parsedRankings = [{
        id: 'all',
        title: '全体ランキング',
        sortIndex: '',
        collectionId: '',
        filters: '',
        ruleContexts: '',
        displayNum: 10,
        showRank: true,
      }];
    }

    this._bindCacheControls();
    this._updateCacheStatus();

    const config = getAlgoliaConfig();
    if (!config?.connected) {
      this._loading = false;
      return;
    }

    try {
      await waitForAlgoliaLibs();
      const searchClient = createSearchClient({ cacheKey: 'algolia-responses-top', maxEntries: 50 });
      if (!searchClient) {
        this._loading = false;
        return;
      }

      const systemFilters = buildSystemFilters(config.customer);

      const queries = this._parsedRankings.map((ranking) => {
        const filterParts: string[] = [];
        if (ranking.collectionId) {
          filterParts.push(`collection_ids:${ranking.collectionId}`);
        }
        if (ranking.filters) {
          filterParts.push(ranking.filters);
        }
        filterParts.push(systemFilters);
        const filters = filterParts.join(' AND ');
        const params: Record<string, unknown> = {
          hitsPerPage: ranking.displayNum,
          distinct: true,
          facetingAfterDistinct: true,
          filters,
          attributesToRetrieve: ['title', 'handle', 'image', 'price', 'compare_at_price', 'price_ratio', 'tags'],
        };
        if (ranking.ruleContexts) {
          params.ruleContexts = ranking.ruleContexts.split(',').map((s) => s.trim());
        }
        return {
          indexName: config.indexName + (ranking.sortIndex || ''),
          params,
        };
      });

      const response = await searchClient.search(queries) as { results: SearchResult[] };

      this._results = response.results.map((result, i) => ({
        ranking: this._parsedRankings[i],
        result,
      }));
      this._loading = false;
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unknown error';
      this._loading = false;
    }
  }

  private _bindCacheControls(): void {
    // Look in parent container (outside this component's Light DOM)
    const container = this.closest('#algolia-top-sim') || document;
    container.querySelectorAll<HTMLButtonElement>('[data-cache-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._toggleCache(btn.dataset.cacheToggle === 'on');
      });
    });
    container.querySelectorAll<HTMLButtonElement>('[data-cache-clear]').forEach((btn) => {
      btn.addEventListener('click', () => this._clearCache());
    });
  }

  private _updateCacheStatus(): void {
    const el = document.getElementById('top-cache-status');
    if (el) {
      el.textContent = this._cacheEnabled ? 'ON (localStorage 5min TTL)' : 'OFF (default in-memory)';
      el.classList.toggle('text-green-400', this._cacheEnabled);
      el.classList.toggle('text-red-400', !this._cacheEnabled);
    }
  }

  private _toggleCache(enabled: boolean): void {
    const url = new URL(window.location.href);
    if (enabled) {
      url.searchParams.set('cache', 'on');
    } else {
      url.searchParams.delete('cache');
    }
    window.location.href = url.toString();
  }

  private _clearCache(): void {
    localStorage.removeItem('algolia-responses-top');
    alert('Top cache cleared');
  }

  private _getRankClass(rank: number): string {
    if (rank === 0) return 'bg-yellow-400 text-gray-700';
    if (rank === 1) return 'bg-gray-300 text-gray-700';
    if (rank === 2) return 'bg-[#CD7F32] text-white';
    return 'bg-gray-700 text-white';
  }

  render() {
    if (this._loading) {
      return html`<div class="text-gray-400 text-[13px] py-5">ランキングデータを読み込み中...</div>`;
    }

    if (this._error) {
      return html`<p class="text-red-500">エラー: ${this._error}</p>`;
    }

    return html`
      ${this._results.map(({ ranking, result }) => html`
        <div class="mb-8">
          <div class="flex justify-between items-center mb-3">
            <h2 class="text-lg font-bold">${ranking.title}</h2>
            <div class="flex items-center gap-3">
              <span class="text-[13px] text-gray-400 font-mono">${result.nbHits}件 / ${result.processingTimeMS}ms</span>
              <a href="${buildMoreUrl(ranking as RankingConfig)}"
                 class="text-[13px] text-indigo-500 no-underline font-semibold">もっと見る →</a>
            </div>
          </div>
          <div class="flex overflow-x-auto gap-3 pb-2 snap-x snap-mandatory ranking-slider">
            ${(result.hits || []).map((hit, rank) => this._renderRankingItem(hit, rank, ranking.showRank))}
          </div>
        </div>
      `)}
    `;
  }

  private _renderRankingItem(hit: RankingHit, rank: number, showRank: boolean) {
    const price = hit.price ? `¥${Number(hit.price).toLocaleString()}` : '';
    const comparePrice = hit.compare_at_price && hit.compare_at_price > (hit.price || 0)
      ? `¥${Number(hit.compare_at_price).toLocaleString()}` : '';
    const offRate = hit.price_ratio ? `${Math.round((1 - hit.price_ratio) * 100)}%OFF` : '';

    return html`
      <div class="flex-none w-[180px] snap-start border border-gray-200 rounded-lg overflow-hidden relative">
        ${showRank
          ? html`<span class="absolute top-1 left-1 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${this._getRankClass(rank)}">${rank + 1}</span>`
          : nothing}
        ${hit.image
          ? html`<img src="${hit.image}" alt="" width="180" height="180" loading="lazy" class="w-[180px] h-[180px] object-cover">`
          : html`<div class="w-[180px] h-[180px] bg-gray-100 flex items-center justify-center text-gray-400 text-xs">画像なし</div>`}
        <div class="p-2">
          <div class="text-xs leading-snug line-clamp-2">${hit.title || ''}</div>
          ${comparePrice && offRate
            ? html`
                <div class="text-[11px] text-gray-400 line-through">${comparePrice}</div>
                <span class="text-sm font-bold text-orange-700">${price}</span>
                <span class="text-[11px] text-orange-700 font-bold"> ${offRate}</span>
              `
            : html`<div class="text-sm font-bold text-orange-700 mt-1">${price}</div>`}
        </div>
      </div>
    `;
  }
}
